// src/common/pages/authorValidation/textos.ts
import type { Idioma } from "./tipos";

/**
 * Los textos de la validación en pasos. Un paso por pantalla y una acción por
 * paso: acá no hay bloques explicativos. Lo que explica para qué se usan los
 * datos vive en la ventana de "Cómo utilizaremos tus datos".
 *
 * La leyenda de la AAIP (Resolución 14/2018, art. 3°) va textual y sin
 * traducir: es la que fija la norma.
 */
export interface TextosPagina {
  pasoFrente: string;
  pasoDorso: string;
  /** Las dos fotos aprobadas, juntas (KAN-460). */
  pasoFotos: string;
  /**
   * El paso que sigue a las fotos (KAN-462), según lo que pide: el teléfono y el código postal;
   * además el domicilio; o además otros datos de la documentación (ver camposDelPasoTelefono).
   */
  pasoTelefono: string;
  pasoTelefonoYDomicilio: string;
  pasoDatosQueFaltan: string;
  /** El resumen final, de solo vista, con «Confirmar mis datos» y «Modificar mis datos». */
  pasoConfirmar: string;
  pasoManual: string;
  /**
   * En qué paso está, para un lector de pantalla: los puntos del indicador solo se ven. `{actual}`
   * y `{total}` son números.
   */
  pasoDeTotal: string;
  sacarFoto: string;
  elegirFoto: string;
  sacarOtra: string;
  continuar: string;
  /** Las dos salidas del resumen: enviar lo que se ve, o modificarlo ahí mismo y enviar. */
  confirmarMisDatos: string;
  modificarMisDatos: string;
  noPuedoUsarDni: string;
  usarMiDni: string;
  /** Hasta el envío final, cada paso deja volver al anterior (recorrido 2). */
  volver: string;
  /**
   * Una foto de la otra cara en el paso de una (recorrido del usuario): si la otra falta, se ofrece
   * usarla como tal; si no, se pide la cara de este paso. Sin explicaciones técnicas.
   */
  pareceDorso: string;
  /** El domicilio leído del DNI reemplaza al guardado (KAN-456): se dice cuál, sin explicaciones. */
  domicilioReemplaza: string;
  pareceFrente: string;
  pareceDorsoFaltaFrente: string;
  pareceFrenteFaltaDorso: string;
  /** Las dos fotos cruzadas: se ofrece intercambiarlas. */
  cruzadas: string;
  si: string;
  reintentar: string;
  /**
   * Mientras se entrega la foto. Su «dos minutos» es el plazo de la entrega
   * (PLAZO_DE_ENTREGA_MS, en dniUpload/useLecturaDni.ts): se cambian juntos, y una
   * prueba de estos textos los ata.
   */
  entregandoFoto: string;
  /** Mientras se envían los datos: nombra al botón, que se queda sin texto. */
  enviandoDatos: string;
  /** El texto alternativo de la vista previa de cada cara y de la propuesta de corte (lectores de pantalla). */
  vistaPreviaFrente: string;
  vistaPreviaDorso: string;
  propuestaDeCorte: string;
  /**
   * Sobre la foto del frente, cuando hay que volver a sacarla (caraAPedir: las dos caras no se
   * aceptan y la que falla es esta): se pide otra foto, sin explicaciones técnicas ni pedir un fondo
   * (decisión del usuario, 2026-10-13).
   */
  otraFotoDelFrente: string;
  /**
   * La espera mientras se procesa una foto (elegirla, sacarla o separar las caras), y su salida,
   * que corta el procesamiento y deja el paso vacío (recorrido de KAN-452).
   */
  procesandoFoto: string;
  cancelar: string;
  /**
   * Sobre la foto del dorso, cuando hay que volver a sacarla (caraAPedir: la aceptación de las dos
   * caras, o dorsoEntregable si algún modelo tuviera medida una zona sensible del dorso): se pide
   * otra foto, sin explicaciones técnicas ni pedir un fondo.
   */
  otraFotoDelDorso: string;
  /**
   * La señal de que una cara se aprobó (KAN-460): un tilde verde con este texto, sin la foto,
   * mientras se pide la otra.
   */
  frenteAprobado: string;
  dorsoAprobado: string;
  /** El nombre de cada foto cuando se ven las dos juntas. */
  frente: string;
  dorso: string;
  /**
   * Junto a la vista previa, cuando la foto lleva una marca negra (el Nº de Trámite o el código de
   * barras tapados con certeza): quitarla, si cayó mal, y la foto se entrega sin tapar; y volver a
   * ponerla (decisión del usuario, 2026-10-13).
   */
  quitarMarca: string;
  ponerMarca: string;
  /**
   * Qué tapa la marca negra, debajo de la foto de cada cara que la lleva (KAN-460): lo que de verdad
   * se tapó en esa cara (loQueTapaElFrente, loQueTapaElDorso).
   */
  marcaTapaTramiteYCodigo: string;
  marcaTapaTramite: string;
  marcaTapaCodigo: string;
  /**
   * Con la marca ya quitada, lo que queda a la vista en esa cara: la línea no puede decir que la
   * marca «tapa» lo que ya no tapa.
   */
  sinMarcaSeVeTramiteYCodigo: string;
  sinMarcaSeVeTramite: string;
  sinMarcaSeVeCodigo: string;
  opcional: string;
  requerido: string;
  emailInvalido: string;
  formatoInvalido: string;
  /**
   * Un CUIT/CUIL que el formulario no pudo llevar a su máscara: dice el formato, con un CUIT de
   * ejemplo con el verificador correcto.
   */
  formatoCuit: string;
  telefonoInvalido: string;
  segunSuDni: string;
  errorDeRed: string;
  errorGenerico: string;
  /**
   * Los rechazos del server al guardar, con el texto del cliente y no el del
   * server. Cada uno en dos formas: con el nombre de la editora en `{editora}`,
   * y sin él, cuando la solicitud no lo trae.
   */
  rechazoDuplicado: string;
  rechazoDuplicadoSinEditora: string;
  rechazoBloqueo: string;
  rechazoBloqueoSinEditora: string;
  rechazoSinCampo: string;
  rechazoSinCampoSinEditora: string;
  /**
   * El texto de un rechazo del server sobre un campo, según su código: el server manda
   * códigos (y su `message` en castellano) y el cliente escribe, en el idioma del autor.
   * Un número repetido y un dato requerido usan `rechazoDuplicado` y `requerido`.
   */
  rechazoOpcionInvalida: string;
  rechazoDatoInvalido: string;
  rechazoRevise: string;
  /**
   * El mensaje final (KAN-457), sin la cuenta de cambios. `{editora}` es quien pide los datos: el
   * seudónimo de su editorial propia si tiene, o su razón social (senderDisplayName en el server,
   * KAN-444). Sin nombre, la otra forma.
   */
  confirmacionTitulo: string;
  confirmacionMensaje: string;
  confirmacionMensajeSinEditora: string;
  /**
   * Debajo del piso o el departamento del domicilio viejo, cuando la calle cambió: lo que había
   * guardado (KAN-456). `{valor}` es ese dato.
   */
  antes: string;
  usoTitulo: string;
  /**
   * Para qué se usan los datos (KAN-453; texto de KAN-461), con lo que de verdad pasa: el DNI va
   * siempre a SADAIC, y el Nº de Trámite y el código de barras se tapan «cuando podamos ubicarlos en
   * la foto» (en el dorso de los DNI 2009 y 2026 su zona no está medida y no se tapa; ver
   * dniUpload/reader/redaccion.ts).
   */
  usoProposito: string;
  usoDerechos: string;
  usoAaip: string;
  cerrar: string;
}

const AAIP =
  "LA AGENCIA DE ACCESO A LA INFORMACIÓN PÚBLICA, en su carácter de Órgano de Control de la Ley N° 25.326, tiene la atribución de atender las denuncias y reclamos que interpongan quienes resulten afectados en sus derechos por incumplimiento de las normas vigentes en materia de protección de datos personales.";

export const TEXTOS_PAGINA: Record<Idioma, TextosPagina> = {
  es: {
    pasoFrente: "Foto del frente de tu DNI",
    pasoDorso: "Foto del dorso de tu DNI",
    pasoFotos: "Fotos de tu DNI",
    pasoTelefono: "Teléfono y código postal",
    pasoTelefonoYDomicilio: "Teléfono y domicilio",
    pasoDatosQueFaltan: "Datos que faltan",
    pasoConfirmar: "¿Son correctos tus datos?",
    pasoManual: "Tus datos",
    pasoDeTotal: "Paso {actual} de {total}",
    sacarFoto: "Sacar foto",
    elegirFoto: "Elegir una foto",
    sacarOtra: "Sacar otra foto",
    continuar: "Continuar",
    confirmarMisDatos: "Confirmar mis datos",
    modificarMisDatos: "Modificar mis datos",
    noPuedoUsarDni: "No puedo usar mi DNI",
    usarMiDni: "Usar mi DNI",
    volver: "Volver",
    pareceDorso: "Esta foto parece ser del dorso de tu DNI. ¿La usamos como dorso?",
    domicilioReemplaza: "Este domicilio reemplaza al que teníamos: {domicilio}.",
    pareceFrente: "Esta foto parece ser del frente de tu DNI. ¿La usamos como frente?",
    pareceDorsoFaltaFrente: "Esta foto parece ser del dorso de tu DNI. Necesitamos una foto del frente.",
    pareceFrenteFaltaDorso: "Esta foto parece ser del frente de tu DNI. Necesitamos una foto del dorso.",
    cruzadas: "Las fotos parecen estar cruzadas: la del frente es del dorso y la del dorso, del frente. ¿Las intercambiamos?",
    si: "Sí",
    reintentar: "Reintentar",
    entregandoFoto: "Enviando la foto de tu DNI. Puede tardar hasta dos minutos.",
    enviandoDatos: "Enviando tus datos…",
    vistaPreviaFrente: "Vista previa de la foto del frente de tu DNI",
    vistaPreviaDorso: "Vista previa de la foto del dorso de tu DNI",
    propuestaDeCorte: "Foto con las dos caras de tu DNI y la línea punteada por donde se cortaría",
    otraFotoDelFrente:
      "Necesitamos otra foto del frente de tu DNI: con el documento entero a la vista, enfocado y bien iluminado.",
    procesandoFoto: "Procesando la foto. Un momento, por favor.",
    cancelar: "Cancelar",
    otraFotoDelDorso:
      "Necesitamos otra foto del dorso de tu DNI: con el documento entero a la vista, enfocado y bien iluminado.",
    frenteAprobado: "Foto del frente aprobada",
    dorsoAprobado: "Foto del dorso aprobada",
    frente: "Frente",
    dorso: "Dorso",
    quitarMarca: "Quitar la marca negra",
    ponerMarca: "Volver a poner la marca negra",
    marcaTapaTramiteYCodigo: "La marca negra tapa el Nº de Trámite y el código de barras.",
    marcaTapaTramite: "La marca negra tapa el Nº de Trámite.",
    marcaTapaCodigo: "La marca negra tapa el código de barras.",
    sinMarcaSeVeTramiteYCodigo: "Sin la marca negra, el Nº de Trámite y el código de barras quedan a la vista.",
    sinMarcaSeVeTramite: "Sin la marca negra, el Nº de Trámite queda a la vista.",
    sinMarcaSeVeCodigo: "Sin la marca negra, el código de barras queda a la vista.",
    opcional: "Opcional",
    requerido: "Campo requerido",
    emailInvalido: "Email inválido",
    formatoInvalido: "Formato inválido",
    formatoCuit: "Formato inválido. Ejemplo: 20-12345678-6.",
    telefonoInvalido: "Teléfono inválido",
    segunSuDni: "Según tu DNI: {texto}",
    errorDeRed: "Error de conexión. Volvé a intentar.",
    errorGenerico: "Ocurrió un error. Volvé a intentar.",
    rechazoDuplicado: "Ya hay otro autor con este dato en {editora}. Comunicate con {editora} para resolverlo.",
    rechazoDuplicadoSinEditora:
      "Ya hay otro autor con este dato. Comunicate con quien te envió este enlace para resolverlo.",
    rechazoBloqueo:
      "Tus datos no se pueden cambiar ahora porque hay documentación en curso con ellos. Comunicate con {editora}.",
    rechazoBloqueoSinEditora:
      "Tus datos no se pueden cambiar ahora porque hay documentación en curso con ellos. Comunicate con quien te envió este enlace.",
    rechazoSinCampo: "No pudimos guardar tus datos. Comunicate con {editora}.",
    rechazoSinCampoSinEditora: "No pudimos guardar tus datos. Comunicate con quien te envió este enlace.",
    rechazoOpcionInvalida: "Elegí una opción de la lista.",
    rechazoDatoInvalido: "Este dato no es válido. Revisalo.",
    rechazoRevise: "Revisá este dato.",
    confirmacionTitulo: "Datos confirmados",
    confirmacionMensaje: "Tus datos fueron verificados y enviados a {editora}. Muchas gracias, podés cerrar esta ventana.",
    confirmacionMensajeSinEditora: "Tus datos fueron verificados y enviados. Muchas gracias, podés cerrar esta ventana.",
    antes: "Antes: {valor}",
    usoTitulo: "Cómo utilizaremos tus datos",
    usoProposito:
      "Tus datos van a ser utilizados para la creación de contratos de edición musical y documentación adjunta. Tu DNI será enviado a la Sociedad Argentina de Autores y Compositores de Música (SADAIC) debido a que es un requisito de la entidad para el ingreso de obras de nuevos autores/compositores. Con el fin de proteger tus datos personales, ocultaremos de la imagen de tu DNI el Nº de Trámite y el código de barras cuando podamos ubicarlos en la foto.",
    usoDerechos:
      "Tus datos se tratan conforme a la Ley 25.326 de Protección de Datos Personales. Podés pedir en cualquier momento acceder a ellos, corregirlos o que se eliminen, escribiéndole a la editorial musical que te envió este pedido.",
    usoAaip: AAIP,
    cerrar: "Cerrar",
  },
  en: {
    pasoFrente: "Photo of the front of your ID",
    pasoDorso: "Photo of the back of your ID",
    pasoFotos: "Photos of your ID",
    pasoTelefono: "Phone and postal code",
    pasoTelefonoYDomicilio: "Phone and address",
    pasoDatosQueFaltan: "Missing details",
    pasoConfirmar: "Is your information correct?",
    pasoManual: "Your details",
    pasoDeTotal: "Step {actual} of {total}",
    sacarFoto: "Take photo",
    elegirFoto: "Choose a photo",
    sacarOtra: "Take another photo",
    continuar: "Continue",
    confirmarMisDatos: "Confirm my details",
    modificarMisDatos: "Edit my details",
    noPuedoUsarDni: "I can't use my ID",
    usarMiDni: "Use my ID",
    volver: "Back",
    pareceDorso: "This photo looks like the back of your ID. Shall we use it as the back?",
    domicilioReemplaza: "This address replaces the one we had: {domicilio}.",
    pareceFrente: "This photo looks like the front of your ID. Shall we use it as the front?",
    pareceDorsoFaltaFrente: "This photo looks like the back of your ID. We need a photo of the front.",
    pareceFrenteFaltaDorso: "This photo looks like the front of your ID. We need a photo of the back.",
    cruzadas: "The photos seem to be swapped: the front one is the back and the back one is the front. Shall we swap them?",
    si: "Yes",
    reintentar: "Try again",
    entregandoFoto: "Sending the photo of your ID. It may take up to two minutes.",
    enviandoDatos: "Sending your details…",
    vistaPreviaFrente: "Preview of the photo of the front of your ID",
    vistaPreviaDorso: "Preview of the photo of the back of your ID",
    propuestaDeCorte: "Photo with both sides of your ID and the dashed line where it would be cut",
    otraFotoDelFrente:
      "We need another photo of the front of your ID: with the whole document in view, in focus and well lit.",
    procesandoFoto: "Processing the photo. One moment, please.",
    cancelar: "Cancel",
    otraFotoDelDorso:
      "We need another photo of the back of your ID: with the whole document in view, in focus and well lit.",
    frenteAprobado: "Front photo approved",
    dorsoAprobado: "Back photo approved",
    frente: "Front",
    dorso: "Back",
    quitarMarca: "Remove the black mark",
    ponerMarca: "Put the black mark back",
    marcaTapaTramiteYCodigo: "The black mark covers the Trámite number and the barcode.",
    marcaTapaTramite: "The black mark covers the Trámite number.",
    marcaTapaCodigo: "The black mark covers the barcode.",
    sinMarcaSeVeTramiteYCodigo: "Without the black mark, the Trámite number and the barcode are visible.",
    sinMarcaSeVeTramite: "Without the black mark, the Trámite number is visible.",
    sinMarcaSeVeCodigo: "Without the black mark, the barcode is visible.",
    opcional: "Optional",
    requerido: "Required field",
    emailInvalido: "Invalid email",
    formatoInvalido: "Invalid format",
    formatoCuit: "Invalid format. Example: 20-12345678-6.",
    telefonoInvalido: "Invalid phone number",
    segunSuDni: "According to your ID: {texto}",
    errorDeRed: "Connection error. Please try again.",
    errorGenerico: "An error occurred. Please try again.",
    rechazoDuplicado: "Another author at {editora} already has this information. Please contact {editora} to resolve it.",
    rechazoDuplicadoSinEditora:
      "Another author already has this information. Please contact whoever sent you this link to resolve it.",
    rechazoBloqueo:
      "Your details cannot be changed right now because there is documentation in progress with them. Please contact {editora}.",
    rechazoBloqueoSinEditora:
      "Your details cannot be changed right now because there is documentation in progress with them. Please contact whoever sent you this link.",
    rechazoSinCampo: "We could not save your details. Please contact {editora}.",
    rechazoSinCampoSinEditora: "We could not save your details. Please contact whoever sent you this link.",
    rechazoOpcionInvalida: "Choose an option from the list.",
    rechazoDatoInvalido: "This information is not valid. Please check it.",
    rechazoRevise: "Please check this information.",
    confirmacionTitulo: "Details confirmed",
    confirmacionMensaje:
      "Your details were verified and sent to {editora}. Thank you very much, you can close this window.",
    confirmacionMensajeSinEditora: "Your details were verified and sent. Thank you very much, you can close this window.",
    antes: "Previously: {valor}",
    usoTitulo: "How we will use your data",
    usoProposito:
      "Your data will be used to create music publishing contracts and their supporting documentation. Your ID will be sent to the Sociedad Argentina de Autores y Compositores de Música (SADAIC) because the society requires it to register works by new authors/composers. To protect your personal data, we'll hide the Trámite number and the barcode from the image of your ID when we can find them in the photo.",
    usoDerechos:
      "Your data is processed under Argentina’s Personal Data Protection Act (Ley 25.326). You can ask at any time to access it, correct it or have it deleted, by writing to the music publisher that sent you this request.",
    usoAaip: AAIP,
    cerrar: "Close",
  },
  pt: {
    pasoFrente: "Foto da frente do seu documento",
    pasoDorso: "Foto do verso do seu documento",
    pasoFotos: "Fotos do seu documento",
    pasoTelefono: "Telefone e código postal",
    pasoTelefonoYDomicilio: "Telefone e endereço",
    pasoDatosQueFaltan: "Dados que faltam",
    pasoConfirmar: "Seus dados estão corretos?",
    pasoManual: "Seus dados",
    pasoDeTotal: "Passo {actual} de {total}",
    sacarFoto: "Tirar foto",
    elegirFoto: "Escolher uma foto",
    sacarOtra: "Tirar outra foto",
    continuar: "Continuar",
    confirmarMisDatos: "Confirmar meus dados",
    modificarMisDatos: "Alterar meus dados",
    noPuedoUsarDni: "Não posso usar meu documento",
    usarMiDni: "Usar meu documento",
    volver: "Voltar",
    pareceDorso: "Esta foto parece ser do verso do seu documento. Podemos usá-la como verso?",
    domicilioReemplaza: "Este endereço substitui o que tínhamos: {domicilio}.",
    pareceFrente: "Esta foto parece ser da frente do seu documento. Podemos usá-la como frente?",
    pareceDorsoFaltaFrente: "Esta foto parece ser do verso do seu documento. Precisamos de uma foto da frente.",
    pareceFrenteFaltaDorso: "Esta foto parece ser da frente do seu documento. Precisamos de uma foto do verso.",
    cruzadas: "As fotos parecem estar trocadas: a da frente é do verso e a do verso, da frente. Podemos trocá-las?",
    si: "Sim",
    reintentar: "Tentar novamente",
    entregandoFoto: "Enviando a foto do seu documento. Pode levar até dois minutos.",
    enviandoDatos: "Enviando seus dados…",
    vistaPreviaFrente: "Pré-visualização da foto da frente do seu documento",
    vistaPreviaDorso: "Pré-visualização da foto do verso do seu documento",
    propuestaDeCorte: "Foto com os dois lados do seu documento e a linha pontilhada por onde seria cortada",
    otraFotoDelFrente:
      "Precisamos de outra foto da frente do seu documento: com o documento inteiro à vista, focado e bem iluminado.",
    procesandoFoto: "Processando a foto. Um momento, por favor.",
    cancelar: "Cancelar",
    otraFotoDelDorso:
      "Precisamos de outra foto do verso do seu documento: com o documento inteiro à vista, focado e bem iluminado.",
    frenteAprobado: "Foto da frente aprovada",
    dorsoAprobado: "Foto do verso aprovada",
    frente: "Frente",
    dorso: "Verso",
    quitarMarca: "Remover a marca preta",
    ponerMarca: "Recolocar a marca preta",
    marcaTapaTramiteYCodigo: "A marca preta cobre o número de Trámite e o código de barras.",
    marcaTapaTramite: "A marca preta cobre o número de Trámite.",
    marcaTapaCodigo: "A marca preta cobre o código de barras.",
    sinMarcaSeVeTramiteYCodigo: "Sem a marca preta, o número de Trámite e o código de barras ficam à vista.",
    sinMarcaSeVeTramite: "Sem a marca preta, o número de Trámite fica à vista.",
    sinMarcaSeVeCodigo: "Sem a marca preta, o código de barras fica à vista.",
    opcional: "Opcional",
    requerido: "Campo obrigatório",
    emailInvalido: "E-mail inválido",
    formatoInvalido: "Formato inválido",
    formatoCuit: "Formato inválido. Exemplo: 20-12345678-6.",
    telefonoInvalido: "Telefone inválido",
    segunSuDni: "Segundo seu documento: {texto}",
    errorDeRed: "Erro de conexão. Tente novamente.",
    errorGenerico: "Ocorreu um erro. Tente novamente.",
    rechazoDuplicado: "Já existe outro autor com este dado em {editora}. Entre em contato com {editora} para resolver.",
    rechazoDuplicadoSinEditora:
      "Já existe outro autor com este dado. Entre em contato com quem lhe enviou este link para resolver.",
    rechazoBloqueo:
      "Seus dados não podem ser alterados agora porque há documentação em andamento com eles. Entre em contato com {editora}.",
    rechazoBloqueoSinEditora:
      "Seus dados não podem ser alterados agora porque há documentação em andamento com eles. Entre em contato com quem lhe enviou este link.",
    rechazoSinCampo: "Não conseguimos salvar seus dados. Entre em contato com {editora}.",
    rechazoSinCampoSinEditora: "Não conseguimos salvar seus dados. Entre em contato com quem lhe enviou este link.",
    rechazoOpcionInvalida: "Escolha uma opção da lista.",
    rechazoDatoInvalido: "Este dado não é válido. Revise-o.",
    rechazoRevise: "Revise este dado.",
    confirmacionTitulo: "Dados confirmados",
    confirmacionMensaje: "Seus dados foram verificados e enviados para {editora}. Muito obrigado, pode fechar esta janela.",
    confirmacionMensajeSinEditora: "Seus dados foram verificados e enviados. Muito obrigado, pode fechar esta janela.",
    antes: "Antes: {valor}",
    usoTitulo: "Como usaremos seus dados",
    usoProposito:
      "Seus dados serão utilizados para a criação de contratos de edição musical e da documentação anexa. Seu documento será enviado à Sociedad Argentina de Autores y Compositores de Música (SADAIC) porque é um requisito da entidade para o registro de obras de novos autores/compositores. Para proteger seus dados pessoais, ocultaremos da imagem do seu documento o número de Trámite e o código de barras quando conseguirmos localizá-los na foto.",
    usoDerechos:
      "Seus dados são tratados conforme a Lei argentina 25.326 de Proteção de Dados Pessoais. Você pode a qualquer momento pedir acesso, correção ou eliminação, escrevendo à editora musical que lhe enviou este pedido.",
    usoAaip: AAIP,
    cerrar: "Fechar",
  },
};

export function textosPagina(idioma: Idioma): TextosPagina {
  return TEXTOS_PAGINA[idioma] ?? TEXTOS_PAGINA.es;
}
